import React, { useCallback, useEffect, useRef } from 'react';
import { motion, Variants } from 'framer-motion';
import { ChevronRight, Plus } from 'lucide-react';
import { useDashboardStore } from '../../store/dashboardStore';
import { useMobileEdgeSwipeBack } from '../../hooks/useMobileEdgeSwipeBack';
import { Staff } from '../../types';

interface StaffListPanelProps {
  context?: 'settings' | 'workflow';
  isMobile?: boolean;
}

export const StaffListPanel: React.FC<StaffListPanelProps> = ({ context = 'settings', isMobile = false }) => {
  const { 
    allStaff,
    setShowStaffFormPanel,
    setEditingStaff,
    currentUser,
    setShowSettingsDatePanel,
    setSchedulePanelSource,
    showSettingsDatePanel,
    setShowAllPatientListPanel,
    setShowSchedulePanel,
    setScheduleSelectedStaffId,
    scheduleSelectedStaffId,
    setShowSchedulePatientListPanel,
    setShowDatePanel,
    showDatePanel,
    showSchedulePanel,
    setSelectedDate,
    setShowWorkflowStaffListPanel,
    setShowStaffListPanel,
    setShowSettingsPanel,
    isNavigatingBack,
    setIsNavigatingBack,
    skipWorkflowStaffListAnimation,
    setSkipWorkflowStaffListAnimation,
    skipFlowSwitchAnimation,
    setSkipFlowSwitchAnimation,
    workflowRootRevealFromSettings,
  } = useDashboardStore();

  const skipEnterAnimation =
    isMobile &&
    context === 'workflow' &&
    (skipWorkflowStaffListAnimation || skipFlowSwitchAnimation || workflowRootRevealFromSettings);

  /** 一覧だけ表示中は「前に選んでいたスタッフ」に色を付けない（カレンダー/スケジュール/設定の日付選択が開いているときだけ） */
  const staffRowHighlightActive =
    context === 'workflow'
      ? !!(showDatePanel || showSchedulePanel)
      : !!(showSettingsDatePanel || showSchedulePanel);

  const staffListSwipeRef = useRef<HTMLDivElement>(null);
  const handleStaffListEdgeBack = useCallback(() => {
    if (context === 'workflow') return;
    setIsNavigatingBack(true);
    setShowStaffListPanel(false);
    setShowSettingsPanel(true);
  }, [context, setIsNavigatingBack, setShowStaffListPanel, setShowSettingsPanel]);
  const { swipeStyle: staffListSwipeStyle } = useMobileEdgeSwipeBack(staffListSwipeRef, {
    isMobile: !!isMobile,
    enabled: !!isMobile,
    onNavigateBack: handleStaffListEdgeBack,
  });

  // 初期表示時（管理者ログイン直後）・フロー切り替え時のアニメーションスキップ後、フラグをクリア
  useEffect(() => {
    if (isMobile && context === 'workflow' && skipWorkflowStaffListAnimation) {
      setSkipWorkflowStaffListAnimation(false);
    }
    if (isMobile && context === 'workflow' && skipFlowSwitchAnimation) {
      setSkipFlowSwitchAnimation(false);
    }
  }, [isMobile, context, skipWorkflowStaffListAnimation, setSkipWorkflowStaffListAnimation, skipFlowSwitchAnimation, setSkipFlowSwitchAnimation]);

  // 管理者権限をチェック
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'developer';

  // 【重要】「スタッフを追加する」ボタンがクリックされた時の処理
  const handleAddStaffClick = () => {
    // 進む動作なので、isNavigatingBackをfalseにリセット
    setIsNavigatingBack(false);
    // 編集対象はいない（null）状態で、編集パネルを開く（＝新規登録モード）
    setEditingStaff(null);
    setShowStaffFormPanel(true);
    // スマホ版では、スタッフ一覧パネルを閉じる
    if (isMobile) {
      setShowStaffListPanel(false);
    }
    // 他パネルを確実に閉じる（設定＞スタッフ追加時の整合性）
    setShowSettingsDatePanel(false);
    setShowAllPatientListPanel(false);
    setShowSchedulePanel(false);
    setShowSchedulePatientListPanel(false);
  };

  // 【重要】既存のスタッフカードがクリックされた時の処理
  const handleStaffSelect = (staff: Staff) => {
    if (context === 'workflow') {
      // 診療フロー用：スタッフ選択でカレンダーパネルを開く
      setScheduleSelectedStaffId(staff.id);
      setShowDatePanel(true);
      return;
    }
    
    // 設定ワークフロー用：編集パネルを開く
    // 進む動作なので、isNavigatingBackをfalseにリセット
    setIsNavigatingBack(false);
    setEditingStaff(staff);
    // スマホ版では、スタッフ一覧パネルを閉じる（PC版では閉じずに右側に展開）
    if (isMobile) {
      setShowStaffListPanel(false);
    }
    // 設定側の日付パネルが開いていたら閉じる
    setShowSettingsDatePanel(false);
    // 患者一覧（設定）も確実に閉じる
    setShowAllPatientListPanel(false);
    // スケジュールも必ず閉じる
    setShowSchedulePanel(false);
    // 予約登録パネル（スケジュール用患者一覧）も必ず閉じる
    setShowSchedulePatientListPanel(false);
  };
  
  // 診療フロー用：スタッフカードクリック時の処理
  const handleWorkflowStaffClick = (staff: Staff) => {
    const { showDatePanel, scheduleSelectedStaffId: currentStaffId, showSchedulePanel } = useDashboardStore.getState();
    const isDifferentStaff = currentStaffId !== staff.id;
    
    // 【重要】スタッフIDを必ず設定（リロード後も正しく動作するように）
    setScheduleSelectedStaffId(staff.id);

    // 【重要】スタッフ選択は「文脈が変わる」ので、申し送り（詳細）と予約登録パネルは常に閉じる
    setShowSchedulePatientListPanel(false);
    useDashboardStore.setState((state) => ({
      showDetailPanel: false,
      selectedPatientId: null,
      detailPanelRevision: state.detailPanelRevision + 1,
    }));
    
    // 別スタッフを選択した場合、または月カレンダーが閉じている場合は開き直す
    if (!showDatePanel || isDifferentStaff) {
      // 【重要】別スタッフを選択した場合は、今日の日付を選択する
      if (isDifferentStaff) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        setSelectedDate(today);
      }
      
      // まず、パネルを閉じる（順序が重要：スタッフIDを変更する前に閉じる）
      // スマホ版では、スタッフ一覧パネルを閉じる
      if (isMobile) {
        setShowWorkflowStaffListPanel(false);
      }
      // スケジュールパネルを閉じる（スタッフ変更時、リビジョンも更新して残像を防ぐ）
      useDashboardStore.setState((state) => ({
        showSchedulePanel: false,
        schedulePanelRevision: state.schedulePanelRevision + 1,
      }));
      setShowSchedulePatientListPanel(false);
      // 月カレンダーを開き直す
      useDashboardStore.setState((state) => ({ 
        datePanelRevision: state.datePanelRevision + 1 
      }));
      setShowDatePanel(true);
    } else if (showDatePanel && !isDifferentStaff) {
      // 【重要】同じスタッフをクリックした場合、月カレンダーを開き直す
      // 月カレンダーを開き直す（revisionを更新）
      useDashboardStore.setState((state) => ({ 
        datePanelRevision: state.datePanelRevision + 1 
      }));
      
      // スケジュールパネルを閉じる（同じスタッフをクリックした時は閉じるだけ）
      if (showSchedulePanel) {
        // 既に開いている場合は、閉じてからrevisionを更新（残像防止）
        useDashboardStore.setState((state) => ({
          showSchedulePanel: false,
          schedulePanelRevision: state.schedulePanelRevision + 1,
        }));
        setShowSchedulePatientListPanel(false);
      }
      // スケジュールパネルを開く処理は削除（日付選択時に開くべき）
    }
  };

  // 戻る動作の時は、前のパネルも左側から開始する
  const panelVariants: Variants = isMobile 
    ? {
        hidden: { x: '100%', opacity: 1 },
        hiddenBack: { x: '-100%', opacity: 1 },
        visible: { x: 0, opacity: 1 },
      }
    : {
        hidden: { x: '-100%' },  // デスクトップ版は左から
        visible: { x: 0 },
      };

  return (
    <div
      ref={staffListSwipeRef}
      style={isMobile ? staffListSwipeStyle : undefined}
      className={
        isMobile
          ? `fixed top-0 left-0 right-0 bottom-0 pt-[var(--header-actual,var(--header-total))] ${context === 'workflow' ? 'z-40' : 'z-[41]'}`
          : 'contents'
      }
    >
    <motion.div
      variants={panelVariants}
      initial={
        skipEnterAnimation
          ? 'visible'
          : isMobile && isNavigatingBack
            ? 'hiddenBack'
            : 'hidden'
      }
      animate="visible"
      exit={
        isMobile
          ? isNavigatingBack
            ? { x: '100%', opacity: 0, transition: { duration: 0.35, ease: 'easeOut' } }
            : { x: '-100%' }
          : undefined
      }
      transition={skipEnterAnimation ? { duration: 0 } : { duration: 0.3, ease: 'easeOut' }}
      className={isMobile 
        ? `h-full w-full bg-white flex flex-col shadow-lg` 
        : `panel-col ${context === 'workflow' ? 'panel-col--workflow' : 'panel-col--settings'} z-30 bg-white border-r flex flex-col shadow-lg`
      }
    >
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {isMobile && context === 'settings' ? (
            <button
              onClick={() => {
                setIsNavigatingBack(true);
                setShowStaffListPanel(false);
                setShowSettingsPanel(true);
              }}
              className="text-lg font-semibold text-gray-800 hover:text-gray-600 transition-colors cursor-pointer"
            >
              スタッフ一覧
            </button>
          ) : (
            <h2 className="text-lg font-semibold text-gray-800">スタッフ一覧</h2>
          )}
        </div>
      </div>
      
      {/* スタッフを追加するボタン（設定ワークフローのみ） */}
      {isAdmin && context === 'settings' && (
        <div className="p-4 border-b">
          <motion.button
            onClick={handleAddStaffClick}
            className="w-full p-4 rounded-lg border border-dashed border-green-200 bg-green-50 hover:bg-green-100 text-green-600 transition-colors"
          >
            <div className="flex items-center justify-center space-x-3">
              <Plus size={20} />
              <span className="font-medium">スタッフを追加する</span>
            </div>
          </motion.button>
        </div>
      )}
      
      <div className={`flex-1 overflow-y-auto ${isMobile ? 'pb-[var(--bottom-menu-total)]' : ''}`}>
        <div className={`p-2 ${context === 'workflow' ? 'space-y-2' : 'space-y-1'}`}>
          {allStaff
            .filter((staff) => staff.name !== '開発者')
            .sort((a, b) => a.name.localeCompare(b.name, 'ja'))
            .map((staff) => {
              const rowSelected = staffRowHighlightActive && scheduleSelectedStaffId === staff.id;
              // 診療フロー: 設定パネルメニューと同型（角丸枠・右 Chevron）
              if (context === 'workflow') {
                const bordered = rowSelected
                  ? 'border-green-200 bg-green-50 [@media(hover:hover)]:hover:bg-green-100 [@media(hover:hover)]:hover:border-green-300'
                  : 'border-gray-200 bg-white [@media(hover:hover)]:hover:bg-gray-50 [@media(hover:hover)]:hover:border-gray-300';
                return (
                  <button
                    key={staff.id}
                    type="button"
                    onClick={() => handleWorkflowStaffClick(staff)}
                    className={`w-full rounded-lg border p-3 text-left flex items-center justify-between gap-3 transition-colors touch-manipulation [-webkit-tap-highlight-color:transparent] cursor-pointer ${bordered}`}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div className="min-w-0 flex-1 text-left">
                        <div className="font-medium text-gray-900 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span>{staff.name}</span>
                          {(staff.role === 'admin' || staff.role === 'developer') && (
                            <span className="text-green-600 text-xs font-semibold shrink-0">管理者</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-gray-400 shrink-0" aria-hidden />
                  </button>
                );
              }
              // 設定フロー: 行に onClick がないので hover は付けない。枠線・角丸は付けず（行はボタンではないため）。
              const rowSurface = rowSelected ? 'bg-green-50' : '';
              return (
            <div
              key={staff.id}
              className={`w-full p-3 rounded-md text-left flex items-center transition-colors touch-manipulation [-webkit-tap-highlight-color:transparent] ${rowSurface} ${
                isAdmin
                    ? ''
                    : 'cursor-not-allowed'
              }`}
            >
               <div className="flex items-center space-x-3 flex-1">
                 <div className="flex-1 text-left">
                   <div className="font-medium text-gray-900 flex items-center space-x-2">
                     <span>{staff.name}</span>
                     {(staff.role === 'admin' || staff.role === 'developer') && (
                       <span className="text-green-600 text-xs font-semibold">管理者</span>
                     )}
                   </div>
                 </div>
               </div>
               {/* 編集・予約登録ボタン（設定ワークフローのみ） */}
               {context === 'settings' && (
                 <div className="flex items-center space-x-2 ml-auto mr-2" onClick={(e) => e.stopPropagation()}>
                   <button
                     type="button"
                     className="px-3 py-1.5 text-sm font-semibold rounded-md border border-gray-400 text-gray-600 bg-white hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-colors cursor-pointer select-none"
                     onClick={() => handleStaffSelect(staff)}
                     aria-label="編集"
                   >
                     編集
                   </button>
                   <button
                     type="button"
                     className="px-3 py-1.5 text-sm font-semibold rounded-md border border-gray-400 text-gray-600 bg-white hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-colors cursor-pointer select-none"
                     onClick={() => { 
                       // 進む動作なので、isNavigatingBackをfalseにリセット
                       setIsNavigatingBack(false);
                       setSchedulePanelSource('settings');
                       // 予約登録では編集パネルは閉じ、日付パネルのみ表示
                       setShowStaffFormPanel(false);
                       // スタッフ一覧パネルを閉じる（PC版では閉じず、スマホ版でのみ閉じる）
                       if (isMobile) {
                         setShowStaffListPanel(false);
                       }
                       // すでに開いている患者一覧（設定）は必ず閉じる
                       setShowAllPatientListPanel(false);
                       // スケジュールも必ず閉じる（再押下時の整合性）
                       setShowSchedulePanel(false);
                       setScheduleSelectedStaffId(staff.id);
                       // 予約登録パネル（スケジュール用患者一覧）も必ず閉じる
                       setShowSchedulePatientListPanel(false);
                       if (showSettingsDatePanel) {
                         // 既に開いている場合は開き直し（リビジョンを直接増加）
                         useDashboardStore.setState((state) => ({ settingsDatePanelRevision: state.settingsDatePanelRevision + 1 }));
                       } else {
                         setShowSettingsDatePanel(true);
                       }
                     }}
                     aria-label="予約登録"
                   >
                     予約登録
                   </button>
                 </div>
               )}
              </div>
            );
            })}
        </div>
      </div>
    </motion.div>
    </div>
  );
};
